import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { attachAnonDiag, getAnonDiag } from "@/lib/anonDiag";
import { Spinner } from "@/components/ui-kit";

// Handles the Emergent Google OAuth redirect. Two modes:
//  - default: sign in / sign up with Google (exchange session_id for an app JWT)
//  - ?link=1: link the Google identity to the already-logged-in account
export default function AuthCallback() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sid = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("session_id");
    const isLink = new URLSearchParams(window.location.search).get("link") === "1";
    const clearUrl = () => window.history.replaceState(null, "", window.location.pathname);
    if (!sid) { nav("/auth", { replace: true }); return; }

    (async () => {
      if (isLink) {
        try {
          const { data } = await api.post("/auth/google/link", { session_id: sid });
          setUser(data.user);
          clearUrl();
          toast.success("Google account linked — you can now use one-tap sign-in.");
          nav("/parent", { replace: true });
        } catch (e) {
          const msg = e.response?.status === 409
            ? "That Google account is already linked to another NumberSense account."
            : "Couldn't link your Google account. Please try again.";
          clearUrl();
          toast.error(msg);
          nav("/parent", { replace: true });
        }
        return;
      }
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sid });
        localStorage.setItem("ns_token", data.access_token);
        setUser(data.user);
        clearUrl();
        toast.success("Welcome!");
        // Mid-funnel Google signups: attach the anonymous diagnostic to a child.
        if (data.user.role === "parent" && getAnonDiag()) {
          const studentId = await attachAnonDiag("");
          if (studentId) { nav(`/student/${studentId}/today?from=diagnostic`, { replace: true }); return; }
        }
        if (data.is_new && data.user.role === "parent") {
          nav("/parent?welcome=1", { replace: true });
        } else {
          nav("/home", { replace: true });
        }
      } catch {
        clearUrl();
        toast.error("Google sign-in failed. Please try again.");
        nav("/auth", { replace: true });
      }
    })();
  }, []);

  return (
    <div className="min-h-screen grid place-items-center bg-[var(--ns-bg)]" data-testid="auth-callback">
      <Spinner label="Signing you in" />
    </div>
  );
}
